/* mt-site-sound — interface sound, the shared core (docs/specs/site-sound.md §5.1).
 *
 * The marker on the line above is load-bearing: SiteSound::MARKER and
 * script/sync_sample_audit.py both use it as a double-inject guard.
 *
 * ONE copy of the synth, the voice table and the preference store, used by both
 * surfaces:
 *
 *   - Hosted audit decks and /sample-audit, where Ruby (SiteSound) or Python
 *     (script/sync_sample_audit.py) inlines this file verbatim into a
 *     self-contained document that has no bundler.
 *   - The landing page, where Vite imports it for its side effect from
 *     app/frontend/lib/sound.ts.
 *
 * That dual life is why it self-registers on a global instead of exporting:
 * `export` would break the inlined copy, and a string eval would break the
 * bundled one. A tweak to a voice therefore reaches every deck ever uploaded
 * AND the site in a single edit.
 *
 * Hard constraints, all deliberate:
 *
 *   - Plain vanilla JS. No imports, no build step, no dependencies.
 *   - It must never break its host. The deck is the sales asset; sound is not.
 *     Every entry point is wrapped and any failure leaves a silent, working page.
 *   - Nothing is synthesized from a file. There is no fetch, no decode and no
 *     licence trail (#1) — 0 KB added to a 644 KB deck.
 *   - No AudioContext exists until the visitor's first gesture (#6). See §4.
 */
(function (g) {
  // Inert on the server. app/javascript/ssr/ssr.tsx never imports the site
  // layer, but this guard means importing it anyway registers nothing rather
  // than throwing on `document`.
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (g.MTSound) return; // inlined twice into one document — keep the first

  var ATTACK = 0.004;
  var FLOOR = 0.0001; // exponential ramps cannot target zero

  // ---- 1. the sound library (§4.1) ------------------------------------------
  // Eight one-shot voices, assignable per event from /admin/controls. The ids
  // here are a contract with SiteSetting::SOUND_LIBRARY on the Ruby side;
  // SiteSettingTest fails if the two lists drift apart.
  //
  // Four engines, because one recipe cannot cover this range:
  //
  //   fm       sine carrier + sine modulator, index collapsing fast. Struck,
  //            hollow, dry. Wood and marimba differ only in ratio and decay.
  //   additive stacked partials at fixed ratios. Inharmonic ratios (2.76) read
  //            as a bell; near-harmonic with a slight detune reads as glass.
  //   noise    a very short lowpassed noise burst plus a low sine for body.
  //            Physical rather than musical — a button, not a note.
  //   pluck    sawtooth through a lowpass that slams shut. String-like.
  //
  // Every voice is short and ends on its own. Nothing here is allowed to ring
  // into the next event.
  var LIBRARY = {
    wood: { kind: "fm", ratio: 3, index: 3, modDecay: 0.06, decay: 0.13, peak: 0.05 },
    marimba: { kind: "fm", ratio: 4, index: 2.4, modDecay: 0.08, decay: 0.24, peak: 0.045 },
    bell: {
      kind: "additive",
      partials: [ [ 1, 1 ], [ 2.76, 0.55 ], [ 5.4, 0.22 ] ],
      detune: 1.5,
      decay: 0.45,
      peak: 0.038
    },
    glass: {
      kind: "additive",
      partials: [ [ 1, 1 ], [ 2, 0.28 ], [ 3, 0.1 ] ],
      detune: 2.5,
      decay: 0.3,
      peak: 0.04
    },
    felt: { kind: "noise", cutoff: 1800, body: 0.55, decay: 0.075, peak: 0.07 },
    pluck: { kind: "pluck", open: 7, close: 1.2, decay: 0.18, peak: 0.04 },
    thud: { kind: "fm", ratio: 1, index: 0.6, modDecay: 0.03, octave: -1, decay: 0.09, peak: 0.07 },
    chime: { kind: "additive", partials: [ [ 1, 1 ], [ 2, 0.3 ] ], decay: 0.25, peak: 0.045,
             second: 1.5, gap: 0.09 },
    none: null // a first-class choice: silence one event without a code change
  };

  // ---- 2. the events (§4.2) -------------------------------------------------
  // An event decides pitch and level; the LIBRARY entry decides timbre. Keeping
  // them separate is what lets a reveal stay quiet and out of the way whichever
  // sound is assigned to it, and lets a slide change keep walking the ladder.
  //
  // `toggle` is deliberately absent from SiteSetting::SOUND_EVENTS: it is
  // feedback about the control itself, not part of the house sound, so it is
  // fixed rather than assignable.
  var EVENTS = {
    slide_change: { freq: 523.25, gain: 1 }, // C5 unless the deck supplies a ladder note
    stage_reveal: { freq: 783.99, gain: 0.36 }, // G5, ~9dB down — it must recede
    cta_click: { freq: 523.25, gain: 1 },
    form_success: { freq: 392.0, gain: 1 },
    toggle: { freq: 392.0, gain: 1, sound: "wood" } // fixed, never configurable
  };

  // Suspended pentatonic, C4 -> C6 (§4.3). Eleven rungs rather than eight: over
  // a 16-slide audit that is a new note on 10 of 15 advances instead of 7, which
  // is what makes "forward rises, backward falls" audible press to press rather
  // than only over a run. No two notes in the set can clash, so arrowing through
  // at speed still sounds intentional.
  var LADDER = [
    261.63, 293.66, 349.23, 392.0, 440.0, 523.25, 587.33, 698.46, 783.99, 880.0, 1046.5
  ];

  // Equal-loudness tilt. Widening the ladder downward bought resolution at the
  // cost of the bottom two rungs, which are the first thing a laptop speaker
  // loses. Boosting them and easing the top back keeps every rung sounding like
  // the same gesture instead of the deck getting louder as it climbs.
  var TILT_PIVOT = 523.25; // C5, treated as unity
  function tilt(freq) {
    var factor = Math.sqrt(TILT_PIVOT / freq);
    return Math.max(0.8, Math.min(1.5, factor));
  }

  // ---- 3. rate limiting (§4.4) ----------------------------------------------
  // Held arrow keys auto-repeat and deck.js churns classes every frame during a
  // drag. Excess triggers are DROPPED, never queued — a queue would turn a fast
  // scrub into a melody that plays after the viewer has stopped.
  var MIN_GAP_MS = 90;
  var MAX_VOICES = 4;

  var PREF_KEY = "mt_sound";

  function prefersReducedMotion() {
    try {
      return !!(
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    } catch (e) {
      return false;
    }
  }

  // Admin settings (SiteSetting, /admin/controls). Injected into decks as part
  // of the snippet and handed to the site from Inertia's shared props. The
  // defaults here are what a page falls back to if the config never arrives —
  // deliberately "on", so a plumbing failure leaves the site as it was rather
  // than silently killing a working feature.
  var DEFAULT_CONFIG = { enabled: true, voices: {} };

  function create(settings) {
    var config = {
      enabled: settings && settings.enabled !== undefined
        ? !!settings.enabled : DEFAULT_CONFIG.enabled,
      voices: (settings && settings.voices) || DEFAULT_CONFIG.voices
    };

    var ctx = null;
    var master = null;
    var lastPlay = 0;
    var live = 0; // voices currently sounding
    var listeners = [];

    // Reduced motion is not what the media query means, but it is the only
    // OS-level signal that exists and it correlates with the sensitivities that
    // matter here. It changes the DEFAULT only — the toggle still turns sound
    // on, so this is never a lockout (#9).
    var fallback = !prefersReducedMotion();
    var memory = null; // used when localStorage throws (private mode)

    function enabled() {
      try {
        var v = window.localStorage.getItem(PREF_KEY);
        if (v === "on") return true;
        if (v === "off") return false;
      } catch (e) {
        /* storage blocked — fall through to the in-memory value */
      }
      if (memory !== null) return memory;
      return fallback;
    }

    function notify(value) {
      for (var i = 0; i < listeners.length; i++) {
        try {
          listeners[i](value);
        } catch (e) {
          /* one bad subscriber must not stop the others */
        }
      }
    }

    function setEnabled(value) {
      var next = !!value;
      memory = next;
      try {
        window.localStorage.setItem(PREF_KEY, next ? "on" : "off");
      } catch (e) {
        /* in-memory for this tab only, same degradation as tracker.js */
      }
      // The kill switch outranks the visitor: if sound is off sitewide, their
      // preference is stored for later but changes nothing they can hear.
      applyGain(next && config.enabled, next ? 0 : 0.02);
      notify(next);
      // Turning OFF cannot announce itself, which is exactly why the control's
      // icon state is required rather than decorative (§8).
      if (next) play("toggle");
    }

    // Muting ramps down over 20ms so a voice cut mid-decay does not click.
    // Unmuting is instantaneous (ramp 0), because the toggle's own confirmation
    // note starts on the same tick and a 20ms fade-in would swallow its 4ms attack.
    function applyGain(on, ramp) {
      if (!master || !ctx) return;
      try {
        var t = ctx.currentTime;
        master.gain.cancelScheduledValues(t);
        master.gain.setValueAtTime(master.gain.value, t);
        if (ramp > 0) master.gain.linearRampToValueAtTime(on ? 1 : 0, t + ramp);
        else master.gain.setValueAtTime(on ? 1 : 0, t);
      } catch (e) {
        /* best effort */
      }
    }

    function onChange(fn) {
      listeners.push(fn);
      return function () {
        var at = listeners.indexOf(fn);
        if (at >= 0) listeners.splice(at, 1);
      };
    }

    // ---- 4. unlocking -------------------------------------------------------
    // Browsers block audio until a user gesture. Rather than building a context
    // at load and leaving it suspended, we build it INSIDE the first gesture, so
    // it is born running: no suspended state, no resume() race, and no silent
    // first interaction to explain (#6).
    //
    // The listeners are capture-phase on `document`, which puts them ahead of
    // every bubble-phase handler on the page — deck.js listens on window, React
    // delegates at its root container. So by the time a slide actually changes
    // or a CTA handler runs, the context already exists and that very same
    // gesture makes its sound.
    var blocked = false; // no Web Audio here at all — stop trying

    function build() {
      if (ctx) return true;
      if (blocked) return true; // "done trying" — releases the gesture listeners
      try {
        var Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) {
          blocked = true;
          return true;
        }
        ctx = new Ctor();
        master = ctx.createGain();
        master.gain.value = enabled() && config.enabled ? 1 : 0;
        master.connect(ctx.destination);
        // Belt and braces for older Safari, which could hand back a suspended
        // context even from inside a gesture.
        if (ctx.state !== "running" && ctx.resume) ctx.resume();
        return true;
      } catch (e) {
        // Constructing it threw once, so it will throw every time. Give up for
        // good rather than re-attempting on every gesture for the whole visit.
        ctx = null;
        master = null;
        blocked = true;
        return true;
      }
    }

    function onFirstGesture() {
      if (build()) release();
    }

    function release() {
      document.removeEventListener("pointerdown", onFirstGesture, true);
      document.removeEventListener("keydown", onFirstGesture, true);
    }

    document.addEventListener("pointerdown", onFirstGesture, true);
    document.addEventListener("keydown", onFirstGesture, true);

    // ---- 5. the engines -----------------------------------------------------
    // Each builds its own graph into `amp` and returns the nodes to start/stop.
    // The shared amplitude envelope and teardown live in note() below, so an
    // engine only has to describe its own timbre.

    function fmVoice(recipe, freq, at, amp) {
      var f = recipe.octave ? freq * Math.pow(2, recipe.octave) : freq;
      var car = ctx.createOscillator();
      var mod = ctx.createOscillator();
      var modGain = ctx.createGain();

      car.type = "sine";
      mod.type = "sine";
      car.frequency.setValueAtTime(f, at);
      mod.frequency.setValueAtTime(f * recipe.ratio, at);

      // Peak frequency deviation is index x modulator frequency. Collapsing it
      // to ~1Hz (never 0 — exponential ramps forbid that) is what takes the
      // brightness out from under the tail.
      modGain.gain.setValueAtTime(recipe.index * f * recipe.ratio, at);
      modGain.gain.exponentialRampToValueAtTime(1, at + recipe.modDecay);
      mod.connect(modGain);
      modGain.connect(car.frequency);
      car.connect(amp);

      return { sources: [ car, mod ], extra: [ modGain ] };
    }

    // Stacked partials. Inharmonic ratios read as a bell; near-harmonic with a
    // couple of Hz of detune reads as glass.
    function additiveVoice(recipe, freq, at, amp) {
      var sources = [];
      var extra = [];
      for (var i = 0; i < recipe.partials.length; i++) {
        var ratio = recipe.partials[i][0];
        var level = recipe.partials[i][1];
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq * ratio, at);
        if (recipe.detune) osc.detune.setValueAtTime(i % 2 ? recipe.detune : -recipe.detune, at);
        gain.gain.setValueAtTime(level, at);
        osc.connect(gain);
        gain.connect(amp);
        sources.push(osc);
        extra.push(gain);
      }
      return { sources: sources, extra: extra };
    }

    // A 30ms lowpassed noise burst for the click, plus a low sine for body.
    // Physical rather than musical — it reads as a button, not a note.
    function noiseVoice(recipe, freq, at, amp) {
      var frames = Math.max(1, Math.floor(ctx.sampleRate * 0.03));
      var buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
      var data = buffer.getChannelData(0);
      for (var i = 0; i < frames; i++) {
        // Fade the burst across the buffer so it cannot click on its own edge.
        data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
      }
      var noise = ctx.createBufferSource();
      noise.buffer = buffer;

      var lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.setValueAtTime(recipe.cutoff, at);
      noise.connect(lp);
      lp.connect(amp);

      var body = ctx.createOscillator();
      var bodyGain = ctx.createGain();
      body.type = "sine";
      body.frequency.setValueAtTime(freq * 0.5, at);
      bodyGain.gain.setValueAtTime(recipe.body, at);
      body.connect(bodyGain);
      bodyGain.connect(amp);

      return { sources: [ noise, body ], extra: [ lp, bodyGain ] };
    }

    // Sawtooth through a lowpass that slams shut — the cheapest convincing
    // plucked string.
    function pluckVoice(recipe, freq, at, amp) {
      var osc = ctx.createOscillator();
      var lp = ctx.createBiquadFilter();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(freq, at);
      lp.type = "lowpass";
      lp.Q.setValueAtTime(1, at);
      // Nyquist is the ceiling: an unclamped sweep start throws on some browsers.
      var open = Math.min(freq * recipe.open, ctx.sampleRate / 2 - 100);
      lp.frequency.setValueAtTime(open, at);
      lp.frequency.exponentialRampToValueAtTime(freq * recipe.close, at + recipe.decay * 0.7);
      osc.connect(lp);
      lp.connect(amp);
      return { sources: [ osc ], extra: [ lp ] };
    }

    var ENGINES = {
      fm: fmVoice,
      additive: additiveVoice,
      noise: noiseVoice,
      pluck: pluckVoice
    };

    // ---- 6. playing ---------------------------------------------------------
    function note(recipe, freq, gain, at) {
      var engine = ENGINES[recipe.kind];
      if (!engine) return;

      var amp = ctx.createGain();
      amp.gain.setValueAtTime(FLOOR, at);
      amp.gain.exponentialRampToValueAtTime(recipe.peak * gain * tilt(freq), at + ATTACK);
      amp.gain.exponentialRampToValueAtTime(FLOOR, at + ATTACK + recipe.decay);
      amp.connect(master);

      var built = engine(recipe, freq, at, amp);
      var stop = at + ATTACK + recipe.decay + 0.02;

      for (var i = 0; i < built.sources.length; i++) {
        built.sources[i].start(at);
        built.sources[i].stop(stop);
      }

      live++;
      built.sources[0].onended = function () {
        live--;
        try {
          amp.disconnect();
          for (var j = 0; j < built.extra.length; j++) built.extra[j].disconnect();
        } catch (e) {
          /* already torn down */
        }
      };
    }

    // Which library sound an event currently uses. Admin config wins; the
    // fallback keeps a deck sounding right even if the config never arrived.
    function soundFor(eventName) {
      var event = EVENTS[eventName];
      if (!event) return null;
      if (event.sound) return LIBRARY[event.sound] || null; // fixed, not configurable
      var chosen = config.voices && config.voices[eventName];
      return LIBRARY[chosen || "wood"] || null;
    }

    // opts.freq overrides the event's default pitch (the deck's ladder uses this).
    function play(eventName, opts) {
      try {
        if (!config.enabled) return; // the admin kill switch (§11)
        if (!enabled()) return; // the visitor's own choice
        if (!ctx || !master) return; // no gesture yet: nothing to unlock from
        if (document.visibilityState !== "visible") return;

        var event = EVENTS[eventName];
        var recipe = soundFor(eventName);
        if (!event || !recipe) return; // "none" lands here, and is meant to

        var now = Date.now();
        if (now - lastPlay < MIN_GAP_MS) return;
        if (live >= MAX_VOICES) return;
        lastPlay = now;

        emit(recipe, (opts && opts.freq) || event.freq, event.gain, ctx.currentTime);
      } catch (e) {
        /* never break the host page */
      }
    }

    // One note, or two for a voice that carries a `second` interval. Both are
    // scheduled up front so the gap is exact and the second is not re-gated.
    function emit(recipe, freq, gain, at) {
      note(recipe, freq, gain, at);
      if (recipe.second) note(recipe, freq * recipe.second, gain, at + recipe.gap);
    }

    // Audition a library sound directly, by id (/admin/controls).
    //
    // Deliberately ignores BOTH gates: Matt has to be able to hear a sound while
    // choosing it, including while the kill switch is off — that is exactly when
    // he is deciding whether to turn it back on. It still needs a real gesture,
    // which pressing the preview button is.
    function preview(soundId, opts) {
      try {
        if (!build()) return;
        if (!ctx || !master) return;
        var recipe = LIBRARY[soundId];
        if (!recipe) return;
        // The master gain tracks the mute state, so bypass it for an audition.
        var was = master.gain.value;
        if (!was) applyGain(true, 0);
        emit(recipe, (opts && opts.freq) || 523.25, 1, ctx.currentTime);
        if (!was) {
          window.setTimeout(function () {
            applyGain(enabled() && config.enabled, 0);
          }, (recipe.decay + 0.3) * 1000);
        }
      } catch (e) {
        /* best effort */
      }
    }

    // Position in the deck -> a rung of the ladder. The last slide always lands
    // on the top note, so the end of an audit sounds finished (§4.3).
    function ladderFreq(index, count) {
      var span = LADDER.length - 1;
      var step = Math.round((index / Math.max(1, count - 1)) * span);
      return LADDER[Math.max(0, Math.min(span, step))];
    }

    // Two tabs of the same domain (a deck and the site, say) stay in step, so a
    // prospect never has to mute twice (#5).
    window.addEventListener("storage", function (e) {
      if (!e || e.key !== PREF_KEY) return;
      var value = enabled();
      applyGain(value && config.enabled, 0.02);
      notify(value);
    });

    return {
      play: play,
      preview: preview,
      enabled: enabled,
      setEnabled: setEnabled,
      onChange: onChange,
      ladderFreq: ladderFreq,
      // Sitewide kill switch (§11). The site reads this to decide whether to
      // render the visitor's toggle at all — a control that cannot do anything
      // is worse than no control.
      available: function () {
        return !!config.enabled;
      },
      // Admin settings can change mid-session on the site: saving /admin/controls
      // is an Inertia navigation, and the new config arrives in shared props.
      setConfig: function (next) {
        if (!next) return;
        if (next.enabled !== undefined) config.enabled = !!next.enabled;
        if (next.voices) config.voices = next.voices;
        applyGain(enabled() && config.enabled, 0.02);
        notify(enabled());
      },
      ready: function () {
        return !!ctx;
      }
    };
  }

  // Memoized: both binding layers ask for "the" sound, and two AudioContexts on
  // one page would double every voice. Settings only apply on the first call —
  // later callers get the live instance and should use setConfig to change it.
  var instance = null;
  g.MTSound = {
    create: function (settings) {
      if (!instance) instance = create(settings);
      else if (settings) instance.setConfig(settings);
      return instance;
    },
    // Exposed so /admin/controls can list and audition the library without
    // duplicating the ids. Order is the order they appear in the dropdown.
    library: function () {
      var out = [];
      for (var id in LIBRARY) if (LIBRARY.hasOwnProperty(id)) out.push(id);
      return out;
    }
  };
})(typeof globalThis !== "undefined" ? globalThis : window);
